import { ReactNode } from "react"
import { useGame } from "@/hooks/useGame"
import formatResultForColor from "@/utils/getResultForColor"
import ScoreLines from "./ScoreLines"
import clsx from "clsx"

const WhiteBackground = ({ compact }: { compact?: boolean }) => {
  const { initialGame } = useGame()
  const { white_player, white_elo, result } = initialGame
  const playerResult = formatResultForColor(result, "white")

  return (
    <div
      className={clsx(
        "flex w-full flex-1 items-center justify-center gap-x-3 bg-white lg:py-0",
        compact ? "py-4" : "py-8 sm:py-12 md:py-16"
      )}
    >
      {playerResult && (
        <div
          className={clsx(
            "bg-chessdrop-500 flex items-center justify-center leading-none font-medium text-white",
            compact
              ? "size-5 rounded text-sm lg:size-7 lg:rounded-md lg:text-base"
              : "size-7 rounded-md text-base"
          )}
        >
          {playerResult}
        </div>
      )}
      {white_player?.name ? (
        <span
          className={clsx(
            "font-medium text-gray-900",
            compact ? "text-base lg:text-lg" : "text-lg"
          )}
        >
          {white_player.name}
        </span>
      ) : null}
      {white_elo ? (
        <span className="mt-0.5 text-sm leading-none text-gray-500">
          {white_elo}
        </span>
      ) : null}
    </div>
  )
}

const BlackBackground = ({ compact }: { compact?: boolean }) => {
  const { initialGame } = useGame()
  const { black_player, black_elo, result } = initialGame
  const playerResult = formatResultForColor(result, "black")

  return (
    <div
      className={clsx(
        "flex w-full flex-1 items-center justify-center gap-x-3 bg-gray-800 lg:py-0",
        compact ? "py-4" : "py-8 sm:py-12 md:py-16"
      )}
    >
      {playerResult && (
        <div
          className={clsx(
            "bg-chessdrop-500 flex items-center justify-center leading-none font-medium text-white",
            compact
              ? "size-5 rounded text-sm lg:size-7 lg:rounded-md lg:text-base"
              : "size-7 rounded-md text-base"
          )}
        >
          {playerResult}
        </div>
      )}
      {black_player?.name ? (
        <span
          className={clsx(
            "font-medium text-white",
            compact ? "text-base lg:text-lg" : "text-lg"
          )}
        >
          {black_player.name}
        </span>
      ) : null}
      {black_elo ? (
        <span className="mt-0.5 text-sm leading-none text-gray-400">
          {black_elo}
        </span>
      ) : null}
    </div>
  )
}

const BoardBackground = ({
  children,
  compact = false,
}: {
  children: ReactNode
  compact?: boolean
}) => {
  const { orientation } = useGame()

  return (
    <div className="relative flex flex-1 flex-col items-center justify-center">
      {orientation === "white" ? (
        <BlackBackground compact={compact} />
      ) : (
        <WhiteBackground compact={compact} />
      )}
      <div className="relative flex w-full items-center justify-center px-6 lg:px-8">
        <ScoreLines />
        <div
          className={clsx(
            "z-30 w-full",
            compact ? "max-w-80 lg:max-w-[500px]" : "max-w-[500px]"
          )}
        >
          {children}
        </div>
      </div>
      {orientation === "white" ? (
        <WhiteBackground compact={compact} />
      ) : (
        <BlackBackground compact={compact} />
      )}
    </div>
  )
}

export default BoardBackground
